import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Navigation, Clock, Mail, ArrowRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const LOCATION_EMAILS = {
  "Katy Showroom": SITE.emails.katy,
  "Copperas Cove Showroom": SITE.emails.cove,
  "Killeen Showroom": SITE.emails.killeen,
};

const mapEmbed = (loc) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(`Gold Time ${loc.lines.join(", ")}`)}&output=embed`;

export default function ShowroomsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Our Showrooms | Gold Time Jewelry",
    canonicalPath: "/showrooms",
    description: "Visit Gold Time Jewelry in Katy, Copperas Cove or Killeen, Texas. Family owned since 1991 — fine jewelry, diamonds, watches, repairs and gold buying. Mon–Sat 10–7, Sun 12–6.",
  });

  return (
    <main data-testid="showrooms-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <Eyebrow>Visit Us</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="showrooms-heading">
              Three showrooms, <span className="text-[#C9A227]">one standard of honesty</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600">
              Walk in for a free, no-pressure valuation — testing and weighing done right in front of
              you at any of our Texas showrooms. Every order placed online can also be picked up at
              the showroom you choose at checkout.
            </p>
          </Reveal>

          <div className="mt-16 space-y-10">
            {SITE.locations.map((loc, i) => (
              <Reveal key={loc.name} delay={0.06 * i}>
                <div
                  data-testid={`showroom-card-${i}`}
                  className="bg-white border border-[#E8DCC4] rounded-sm overflow-hidden grid lg:grid-cols-2 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                >
                  <div className="p-8 lg:p-10">
                    <h2 className="font-display text-3xl text-neutral-900">{loc.name}</h2>
                    <div className="mt-6 space-y-4 text-sm text-neutral-600">
                      <p className="flex items-start gap-3 leading-relaxed">
                        <MapPin size={16} strokeWidth={1.5} className="text-[#C9A227] shrink-0 mt-0.5" />
                        <span>
                          {loc.lines.map((line) => (
                            <span key={line} className="block">{line}</span>
                          ))}
                        </span>
                      </p>
                      <a
                        href={loc.phoneHref}
                        data-testid={`showroom-phone-${i}`}
                        className="flex items-center gap-3 font-semibold text-neutral-900 hover:text-[#C9A227] transition-colors duration-300"
                      >
                        <Phone size={16} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                        {loc.phone}
                      </a>
                      <a
                        href={`mailto:${LOCATION_EMAILS[loc.name]}`}
                        data-testid={`showroom-email-${i}`}
                        className="flex items-center gap-3 hover:text-[#A8871B] transition-colors duration-300"
                      >
                        <Mail size={16} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                        {LOCATION_EMAILS[loc.name]}
                      </a>
                      <div className="flex items-start gap-3" data-testid={`showroom-hours-${i}`}>
                        <Clock size={16} strokeWidth={1.5} className="text-[#C9A227] shrink-0 mt-0.5" />
                        <span>
                          {SITE.hours.map((h) => (
                            <span key={h.days} className="block leading-relaxed">
                              <span className="text-neutral-500">{h.days}:</span> {h.time}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <a
                        href={loc.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`showroom-directions-${i}`}
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300"
                      >
                        <Navigation size={14} strokeWidth={1.5} /> Get directions
                      </a>
                      <Link
                        to={`/contact?topic=${encodeURIComponent("General question")}`}
                        data-testid={`showroom-message-${i}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
                      >
                        Message this showroom <ArrowRight size={14} strokeWidth={1.5} />
                      </Link>
                    </div>
                  </div>
                  <div className="min-h-[280px] lg:min-h-0 bg-[#F5F3EC]">
                    <iframe
                      title={`Map to ${loc.name}`}
                      src={mapEmbed(loc)}
                      loading="lazy"
                      data-testid={`showroom-map-${i}`}
                      className="w-full h-full min-h-[280px] border-0"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
